import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { withRouter, RouteComponentProps } from 'react-router';
import classnames from 'classnames';
import Styled from 'styled';
import PhotoSwipeRoot, { bindEventsToRoot } from 'photoswipe/root/root';
import DefaultUi from 'photoswipe/default-ui/default-ui';
import Photoswipe from 'photoswipe';
import PhotoswipeDefaultUI from 'photoswipe/dist/photoswipe-ui-default';
import dots from 'four-nets/icons/svg/icon_menu.svg';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import { gemiusHit } from 'lib/gemius/gemius';
import { keyEscapePressed } from 'lib/key-code/key-code';

import close from 'four-nets/icons/svg/icon_close.svg';
import { Site } from 'four-nets/site';
import { ModalRefContext } from 'four-nets/ui-v2/modal/modal';

import ImageMore from './image-more/image-more';
import Description from './description/description';
import DescriptionUi from './description/ui/ui';
import Ui from './ui/ui';
import { Props as ImageProps } from './image/image';
import defaultStyles from './feed-images.scss';

import { FeedImagesFragment } from './feed-images.graphql';
import EventBarrier from 'photoswipe/event-barrier/event-barrier';
import { PhotoPresentationMobileBranding } from 'lib/google/dfp/ad-units/branding';

export type ImageNode = NonNullable<NonNullable<NonNullable<FeedImagesFragment['images']>['edges']>[0]>['node'];
export type OnClick = (
    event: React.MouseEvent<HTMLDivElement | HTMLButtonElement | React.ReactNode>,
    { index }: ImageProps
) => void;

export interface Props extends Partial<FeedImagesFragment>, RouteComponentProps<any> {
    styles: typeof defaultStyles;
    children: (
        imgRef: ((element: HTMLImageElement | null, { id }: ImageProps) => void) | React.RefObject<HTMLDivElement>,
        onClick: (
            event: React.MouseEvent<HTMLDivElement | HTMLButtonElement | React.ReactNode>,
            { index }: ImageProps
        ) => void
    ) => void;
    imageId?: string;
    commentId?: string;
    fetchMore?: (all?: boolean) => Promise<any>;
    fetchMoreIndex?: number;
    resetFetchMore?: () => void;
    unshift?: boolean;
    newImagesCount?: number;
    useExternalImgLinks?: boolean;
}

interface State {
    id?: string;
    node?: ImageNode;
    open?: boolean;
    moreOpen?: boolean;
    photoswipe?: PhotoSwipe<PhotoSwipe.Options> | null;
    index?: number;
    closing?: boolean;
    imageId?: string;
    commentId?: string;
    loading?: boolean;
}

const UI_OPTIONS = {
    closeEl: true,
    captionEl: true,
    fullscreenEl: false,
    zoomEl: false,
    shareEl: false,
    counterEl: false,
    arrowEl: false,
    preloaderEl: true,
    clickToCloseNonZoomable: false,
    closeElClasses: ['caption', 'zoom-wrap', 'ui', 'top-bar'],
};

class FeedImages extends React.PureComponent<Props, State> {
    static defaultProps = {
        fetchMoreIndex: 2,
    };

    static contextTypes = {
        site: PropTypes.instanceOf(Site),
    };

    static getDerivedStateFromProps: React.GetDerivedStateFromProps<Props, State> = (props, state) => {
        let newState = state;

        /* We need to update internal state when data in cache updates. */
        if (state.node && props.images) {
            const { id } = state.node;
            for (const edge of props.images!.edges!) {
                if (edge!.node!.id === id) {
                    newState = {
                        ...newState,
                        node: edge!.node,
                    };
                }
            }
        }

        const { imageId, commentId } = state;
        /* Open Modal when comming from notification */
        if (
            (props.imageId && props.imageId !== imageId) ||
            (props.commentId && props.commentId !== commentId && props.images)
        ) {
            for (let i = 0; i < props.images!.edges!.length; i++) {
                const edge = props.images!.edges![i];
                if (edge!.node!.id === props.imageId) {
                    newState = {
                        ...newState,
                        index: i,
                        open: true,
                        closing: false,
                        node: edge!.node,
                        imageId: props.imageId,
                        commentId: props.commentId,
                    };
                }
            }
        }

        return newState;
    };

    state: State = {};
    refMap = new Map<string, HTMLImageElement>();
    node: HTMLDivElement | null = null;
    initialPath: string | null = null;

    onResize = () => {
        if (this.state.photoswipe) {
            this.state.photoswipe.updateSize(true);
        }
    };

    onPopState = () => {
        if (this.state.photoswipe) {
            this.state.photoswipe.close();
        }
    };

    componentDidMount() {
        this.initialPath = window.location.pathname + window.location.search;
        window.addEventListener('resize', this.onResize);
        window.addEventListener('popstate', this.onPopState);
        document.addEventListener('keydown', this.onKeyDown);
    }

    componentDidUpdate() {
        const { open } = this.state;
        const { styles } = this.props;

        if (open) {
            window.document.body.classList.add(styles.bodyLockScroll);
        } else {
            window.document.body.classList.remove(styles.bodyLockScroll);
        }
    }

    componentWillUnmount() {
        const { styles } = this.props;

        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('popstate', this.onPopState);
        document.removeEventListener('keydown', this.onKeyDown);
        window.document.body.classList.remove(styles.bodyLockScroll);
    }

    itemize = (edges: NonNullable<FeedImagesFragment['images']>['edges']) => {
        return edges.map(edge => {
            const { id, photoFile } = edge!.node!;
            let msrc;

            const ref = this.refMap.get(id);

            if (ref) {
                msrc = ref.currentSrc || ref.src;
            }

            return {
                id,
                node: edge!.node!,
                w: photoFile!.width!,
                src: photoFile!.src!,
                h: photoFile!.height!,
                msrc,
            };
        });
    };

    items = () => {
        return (this.props.images && this.itemize(this.props.images.edges)) || [];
    };

    openMore = (event: React.MouseEvent | React.TouchEvent) => {
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation();

        this.setState({ moreOpen: true });
    };

    closeMore = () => {
        this.setState({ moreOpen: false });
    };

    photoSwipeFetchMore = () => {
        const { loading } = this.state;
        const { fetchMore } = this.props;

        if (this.state.photoswipe && fetchMore && !loading) {
            this.setState({ loading: true });

            fetchMore()
                .then(result => {
                    const { imagesCount, newImagesCount, unshift } = this.props;

                    const edges =
                        (result &&
                            result.data &&
                            result.data.collections &&
                            result.data.collections.edges &&
                            result.data.collections.edges.length &&
                            result.data.collections.edges[0] &&
                            result.data.collections.edges[0].node &&
                            result.data.collections.edges[0].node.rawImages &&
                            result.data.collections.edges[0].node.rawImages.edges) ||
                        (result &&
                            result.data &&
                            result.data.post &&
                            result.data.post.images &&
                            result.data.post.images.edges) ||
                        (result &&
                            result.data &&
                            result.data.feed &&
                            result.data.feed.edges &&
                            result.data.feed.edges.length &&
                            result.data.feed.edges[0] &&
                            result.data.feed.edges[0].node &&
                            result.data.feed.edges[0].node.images &&
                            result.data.feed.edges[0].node.images.edges) ||
                        (result && result.data && result.data.popularImages && result.data.popullarImages.edges) ||
                        (result &&
                            result.data &&
                            result.data.tagSlugs &&
                            result.data.tagSlugs.edges &&
                            result.data.tagSlugs.edges.length &&
                            result.data.tagSlugs.edges[0] &&
                            result.data.tagSlugs.edges[0].node &&
                            result.data.tagSlugs.edges[0].node.tag &&
                            result.data.tagSlugs.edges[0].node.tag.images &&
                            result.data.tagSlugs.edges[0].node.tag.images.edges) ||
                        (result &&
                            result.data &&
                            result.data.vendors &&
                            result.data.vendors.edges &&
                            result.data.vendors.edges[0].node &&
                            result.data.vendors.edges[0].node.album &&
                            result.data.vendors.edges[0].node.album.images &&
                            result.data.vendors.edges[0].node.album.images.edges) ||
                        (result &&
                            result.data &&
                            result.data.postBySlug &&
                            result.data.postBySlug.images &&
                            result.data.postBySlug.images.edges) ||
                        (result &&
                            result.data &&
                            result.data.users &&
                            result.data.users.edges &&
                            result.data.users.edges[0].node &&
                            result.data.users.edges[0].node.photoBlog &&
                            result.data.users.edges[0].node.photoBlog.weddingAlbum &&
                            result.data.users.edges[0].node.photoBlog.weddingAlbum.images &&
                            result.data.users.edges[0].node.photoBlog.weddingAlbum.images.edges);

                    if (edges && this.state.open && this.state.photoswipe) {
                        const newItems = this.itemize(edges);
                        const photoswipe = this.state.photoswipe;

                        if (this.props.unshift && newImagesCount) {
                            if (newImagesCount < 3) {
                                const notNewImages = photoswipe.items.splice(newImagesCount);
                                photoswipe.items.unshift(...newItems);
                                photoswipe.items.unshift(...notNewImages);
                            } else {
                                photoswipe.items.push(...newItems.slice(0, newImagesCount - 3));
                                photoswipe.items.unshift(...newItems.slice(newImagesCount - 3));
                            }
                        } else {
                            photoswipe.items.push(...newItems);
                        }

                        if (unshift && imagesCount && newImagesCount) {
                            let goTo = 0;
                            if (photoswipe.getCurrentIndex() > newImagesCount - 1) {
                                goTo = photoswipe.getCurrentIndex() - newImagesCount;
                            } else {
                                goTo = photoswipe.getCurrentIndex() + imagesCount - newImagesCount;
                            }
                            photoswipe.goTo(goTo);
                        }
                        photoswipe.invalidateCurrItems();
                        photoswipe.updateSize(true);
                        (photoswipe.ui as any).update();
                    }
                })
                .finally(() => {
                    this.setState({ loading: false });
                });
        }
    };

    init = (ref: HTMLDivElement | null) => {
        if (ref) {
            const items = this.items();
            const index = this.state.index || 0;
            const item = items[index];

            const photoswipe = bindEventsToRoot(
                new Photoswipe(ref, PhotoswipeDefaultUI, items, {
                    ...UI_OPTIONS,
                    index,
                    closeOnScroll: false,
                    closeOnVerticalDrag: true,
                    modal: false,
                    escKey: false,
                    history: false,
                    loop: false,
                    timeToIdle: 0,
                })
            );

            photoswipe.listen('beforeChange', () => {
                const { fetchMoreIndex = 2, fetchMore } = this.props;
                const currentIndex = photoswipe.getCurrentIndex();

                if (currentIndex >= items.length - fetchMoreIndex && fetchMore) {
                    this.photoSwipeFetchMore();
                }
            });

            photoswipe.listen('afterChange', () => {
                const currentIndex = photoswipe.getCurrentIndex();
                const newItem = items[currentIndex];

                if (newItem) {
                    window.history.replaceState(null, '', newItem.node.href);
                    // track pageview in gemius manualy because we change
                    // url outside of react router so it won't get
                    // tracked in <Gemius> component
                    gemiusHit(this.context.site.getGemiusCode(newItem.node.href));
                    this.setState({
                        node: newItem.node,
                        id: newItem.id,
                    });
                }
            });

            photoswipe.listen('close', () => {
                this.setState({
                    closing: true,
                });
            });

            photoswipe.listen('destroy', () => {
                this.setState({ open: false, photoswipe: null, closing: false }, () => {
                    if (this.props.resetFetchMore) {
                        this.props.resetFetchMore();
                    }
                    if (this.initialPath) {
                        this.props.history.replace(this.initialPath);
                    }
                });
            });

            this.setState(
                {
                    photoswipe,
                    id: item && item.id,
                    node: item && item.node,
                },
                () => {
                    if (this.state.photoswipe === photoswipe) {
                        window.history.pushState(null, '', item.node.href);
                        photoswipe.init();
                    }
                }
            );
        }
    };

    close = () => {
        if (this.state.photoswipe) {
            this.state.photoswipe.close();
        }
    };

    imgRef = (element: HTMLImageElement | null, { id }: ImageProps) => {
        if (element) {
            this.refMap.set(id, element);
        } else {
            this.refMap.delete(id);
        }
    };

    onKeyDown = (event: KeyboardEvent) => {
        if (keyEscapePressed(event as unknown as React.KeyboardEvent)) {
            this.close();
        }
    };

    render() {
        const { styles, commentId, imageId, imagesCount, newImagesCount, useExternalImgLinks = false } = this.props;
        const { photoswipe, node, closing, open, moreOpen } = this.state;

        return (
            <React.Fragment>
                {this.props.children(this.imgRef, (event, { index, ...other }) => {
                    if (other.post && other.post.postType == 'POST_ALBUM') {
                        window.open(window.location.origin + other.href, '_blank');
                        return;
                    }

                    this.setState({
                        open: true,
                        closing: false,
                        index,
                    });
                })}
                <ModalRefContext.Consumer>
                    {ref =>
                        ref && open
                            ? ReactDOM.createPortal(
                                  <>
                                      <div
                                          className={classnames(styles.wrapper, {
                                              [styles.closing]: closing,
                                          })}
                                      >
                                          <div
                                              style={{
                                                  display: 'flex',
                                                  flexDirection: 'column',
                                                  width: '100%',
                                                  height: '100%',
                                              }}
                                          >
                                              <PhotoPresentationMobileBranding />

                                              <div style={{ display: 'flex', flexGrow: 1 }}>
                                                  <div className={styles.container}>
                                                      <button
                                                          className={classnames(styles.close, {
                                                              [styles.hide]: closing,
                                                          })}
                                                          onClick={this.close}
                                                      >
                                                          <SVGIcon style={{ image: styles.icon }} src={close} />
                                                      </button>

                                                      <PhotoSwipeRoot forwardedRef={this.init} modal={false}>
                                                          <DefaultUi
                                                              style={{ buttonClose: styles.buttonClose }}
                                                              {...UI_OPTIONS}
                                                              loading={this.state.loading}
                                                              topBarAfter={
                                                                  <EventBarrier>
                                                                      <button
                                                                          className={styles.more}
                                                                          onTouchEnd={this.openMore}
                                                                          onClick={this.openMore}
                                                                      >
                                                                          <SVGIcon
                                                                              style={{ image: styles.icon }}
                                                                              src={dots}
                                                                          />
                                                                      </button>
                                                                  </EventBarrier>
                                                              }
                                                          >
                                                              <Ui
                                                                  photoswipe={photoswipe}
                                                                  unshift={this.props.unshift}
                                                                  newImagesCount={newImagesCount}
                                                                  total={imagesCount || undefined}
                                                              />
                                                              {node ? (
                                                                  <DescriptionUi
                                                                      {...node}
                                                                      commentId={commentId}
                                                                      imageId={imageId}
                                                                  />
                                                              ) : null}
                                                          </DefaultUi>
                                                      </PhotoSwipeRoot>
                                                  </div>
                                                  <div className={styles.comments}>
                                                      {node ? (
                                                          <Description
                                                              {...node}
                                                              commentId={commentId}
                                                              onMore={this.openMore}
                                                          />
                                                      ) : null}
                                                  </div>
                                              </div>
                                          </div>
                                      </div>
                                      {node ? (
                                          <ImageMore
                                              node={node}
                                              photoswipe={photoswipe}
                                              open={moreOpen || false}
                                              onClose={this.closeMore}
                                          />
                                      ) : null}
                                  </>,
                                  ref
                              )
                            : null
                    }
                </ModalRefContext.Consumer>
            </React.Fragment>
        );
    }
}

export default Styled(defaultStyles)(withRouter(FeedImages));
